/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { map, omit, isEqual, filter, find } from 'lodash';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon, Form, Message } from 'semantic-ui-react';
import { isMongoId } from '../../../../lib/formValidation';

import { Modal, Button, FormLabel } from '../../../../components';
import FieldList from './FieldList';
import { GET_SUPPLIERS_QUERY } from '../../containers/queries';

export const GET_SALON_LIST_QUERY = gql`
    query getSalonList($resolveRole: String) {
        viewer(resolveRole: $resolveRole) {
            id
            ... on Sysadmin {
                salonList {
                    id
                    name
                    administrator {
                        id
                        firstName
                        lastName
                        phone
                    }
                }
                manufacturerList {
                    id
                    name
                    salon
                    deletedAt
                }
            }
        }
    }
`;

const CREATE_CONNECTION_QUERY = gql`
    mutation createSupplierSalonConnection(
        $input: CreateSupplierSalonConnectionInput!
    ) {
        createSupplierSalonConnection(input: $input) {
            supplier
            connections {
                salon
                manufacturer
            }
        }
    }
`;

const intlMessages = defineMessages({
    atLeastOneError: {
        id: 'pages.suppliers.supplierForm.atLeastOneError',
        defaultMessage: 'Add atleast one item',
    },
    selectSalonError: {
        id: 'pages.suppliers.supplierForm.salonSelectError',
        defaultMessage: 'Select salon',
    },
    selectManufacturerError: {
        id: 'pages.suppliers.supplierForm.selectManufacturerError',
        defaultMessage: 'Select manufacturer',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

type SalonConnectionFormProps = {
    supplier: Object,
    onClose: Function,
    intl: Object,
};

function SalonConnectionForm({ supplier, onClose, intl }: SalonConnectionFormProps) {
    const searchQuery = useSelector(({ suppliers }) => suppliers.get('searchQuery'));

    const { data, loading } = useQuery(GET_SALON_LIST_QUERY, {
        variables: { resolveRole: 'SYSADMIN' },
        fetchPolicy: 'cache-and-network',
    });

    const salons = (!loading && data && data.viewer && data.viewer.salonList) || [];
    const manufacturers = (!loading && data && data.viewer && data.viewer.manufacturerList) || [];

    const buildInitialItems = () =>
        map(supplier.connections, item => omit(item, ['__typename']));

    const methods = useForm({
        defaultValues: { items: buildInitialItems() },
    });

    const {
        handleSubmit,
        reset,
        setError,
        watch,
        formState: { isSubmitting, errors },
    } = methods;

    // Reinitialize when supplier connections change
    useEffect(() => {
        reset({ items: buildInitialItems() });
    }, [supplier.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const [createSupplierSalonConnection] = useMutation(CREATE_CONNECTION_QUERY, {
        update: (store, { data: { createSupplierSalonConnection: result } }) => {
            try {
                const variables = {
                    search: searchQuery || undefined,
                    first: 20,
                    resolveRole: 'SYSADMIN',
                };
                const storeData = store.readQuery({
                    query: GET_SUPPLIERS_QUERY,
                    variables,
                });
                const item = find(storeData.viewer.suppliers.edges, [
                    'node.id',
                    result.supplier,
                ]);
                if (item) {
                    item.node.connections = result.connections;
                }
                store.writeQuery({
                    query: GET_SUPPLIERS_QUERY,
                    variables,
                    data: storeData,
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const validateItems = items => {
        if (!items || items.length === 0) {
            return intl.formatMessage(intlMessages.atLeastOneError);
        }
        return true;
    };

    const onSubmit = async formData => {
        const validItems = filter(
            formData.items,
            item => item && item.salon && item.manufacturer,
        );

        // Validate items
        for (let idx = 0; idx < (formData.items || []).length; idx++) {
            const item = formData.items[idx];
            if (!item || !isMongoId(item.salon)) {
                setError(`items[${idx}].salon`, { message: intl.formatMessage(intlMessages.selectSalonError) });
                return;
            }
            if (!item || !isMongoId(item.manufacturer)) {
                setError(`items[${idx}].manufacturer`, { message: intl.formatMessage(intlMessages.selectManufacturerError) });
                return;
            }
        }

        if (!formData.items || formData.items.length === 0) {
            setError('root', { message: intl.formatMessage(intlMessages.atLeastOneError) });
            return;
        }

        try {
            await createSupplierSalonConnection({
                variables: {
                    input: {
                        supplier: supplier.id,
                        connections: formData.items,
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    createSupplierSalonConnection: {
                        __typename: 'CreateSupplierSalonConnectionPayload',
                        supplier: supplier.id,
                        connections: formData.items,
                    },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                setError('root', { message: graphQLError.message });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    // Check if form is pristine (unchanged from initial connections)
    const currentItems = watch('items') || [];
    const initialItems = buildInitialItems();
    const isPristine =
        isEqual(
            initialItems.sort(),
            filter(currentItems, item => item && item.salon && item.manufacturer).sort(),
        ) || currentItems.length === 0;

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    <Form error={!!errors.root}>
                        {errors.root && (
                            <Message error>
                                <Icon name="warning sign" />
                                {errors.root.message}
                            </Message>
                        )}
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.suppliers.supplierForm.salonConnection.items"
                                    defaultMessage="Connections to salons and manufacturers"
                                />
                            </FormLabel>
                            <FieldList
                                salons={salons}
                                manufacturers={manufacturers}
                            />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || isPristine}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.suppliers.editSupplier.button.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.suppliers.editSupplier.button.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(SalonConnectionForm);
